import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _isEqual from "lodash/isEqual";
import { StatsigProvider as BaseStatsigProvider } from '@statsig/react-bindings';
import produce from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { UnexpectedError } from '../../error';
import { AppName, createLogger } from '../../logging';
import { createContext } from '../../react';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = createLogger(AppName.Common, 'experiment');
const INITIALIZE_TIMEOUT_MS = 10000;
const INITIALIZE_LOG_SAMPLE_RATE = 0.005;
export const [useExperimentContext, ExperimentContext] = createContext();
export const ExperimentProvider = ({
  children,
  client,
  initializeUser,
  fallback
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (isInitialized) {
      return undefined;
    }
    let cancelled = false;
    const fetchingUserData = function () {
      var _ref = _asyncToGenerator(function* () {
        const updatedUser = yield initializeUser(client.getContext().user);
        yield client.dataAdapter.prefetchData(updatedUser);
        client.updateUserSync(updatedUser);
      });
      return function fetchingUserData() {
        return _ref.apply(this, arguments);
      };
    }();
    let timeoutId;
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`Initialization not settled within ${INITIALIZE_TIMEOUT_MS}ms`));
      }, INITIALIZE_TIMEOUT_MS);
    });
    Promise.race([fetchingUserData(), timeout]).catch(err => {
      if (cancelled) return;
      const error = err instanceof Error ? err : new Error(String(err));
      logger.log('Fail to initialize experiment client', error.message);
      if (Math.random() < INITIALIZE_LOG_SAMPLE_RATE) {
        logger.raise(new UnexpectedError('Fail to initialize experiment client', error));
      }
    }).finally(() => {
      clearTimeout(timeoutId);
      if (cancelled) return;
      client.initializeSync();
      setIsInitialized(true);
    });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [client, initializeUser, isInitialized]);
  const [{
    loading: loadingUpdateUserId
  }, updateUserId] = useAsyncFn(function () {
    var _ref2 = _asyncToGenerator(function* (idKey, id) {
      if (!isInitialized) return;
      try {
        var _prevUser$customIDs, _prevUser$customIDs2;
        const prevUser = client.getContext().user;
        if ((_prevUser$customIDs = prevUser.customIDs) !== null && _prevUser$customIDs !== void 0 && _prevUser$customIDs[idKey] && ((_prevUser$customIDs2 = prevUser.customIDs) === null || _prevUser$customIDs2 === void 0 ? void 0 : _prevUser$customIDs2[idKey]) === id) {
          return prevUser;
        }
        const updated = produce(prevUser, draft => {
          if (draft.customIDs) {
            draft.customIDs[idKey] = id;
          } else {
            draft.customIDs = {
              [idKey]: id
            };
          }
        });
        yield client.dataAdapter.prefetchData(updated);
        client.updateUserSync(updated);
        return updated;
      } catch (err) {
        throw new UnexpectedError('Fail to update user custom id', err);
      }
    });
    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }(), [client, isInitialized]);
  const [{
    loading: loadingUpdateUserEmail
  }, updateUserEmail] = useAsyncFn(function () {
    var _ref3 = _asyncToGenerator(function* (email) {
      if (!isInitialized) return;
      try {
        const prevUser = client.getContext().user;
        if (prevUser.email === email) return prevUser;
        const updated = produce(prevUser, draft => {
          draft.email = email;
        });
        yield client.dataAdapter.prefetchData(updated);
        client.updateUserSync(updated);
        return updated;
      } catch (err) {
        throw new UnexpectedError('Fail to update user email', err);
      }
    });
    return function (_x3) {
      return _ref3.apply(this, arguments);
    };
  }(), [client, isInitialized]);
  const [{
    loading: loadingUpdateUserProfile
  }, updateUserProfile] = useAsyncFn(function () {
    var _ref4 = _asyncToGenerator(function* (profileData) {
      if (!isInitialized) return;
      try {
        const prevUser = client.getContext().user;
        const updated = produce(prevUser, draft => {
          draft.custom = {
            ...(draft.custom || {}),
            ...profileData
          };
        });
        if (_isEqual(prevUser.custom, updated.custom)) {
          return prevUser;
        }
        yield client.dataAdapter.prefetchData(updated);
        client.updateUserSync(updated);
        return updated;
      } catch (err) {
        throw new UnexpectedError('Fail to update user profile', err);
      }
    });
    return function (_x4) {
      return _ref4.apply(this, arguments);
    };
  }(), [client, isInitialized]);
  const value = useMemo(() => ({
    updateUserId,
    updateUserEmail,
    updateUserProfile,
    loading: loadingUpdateUserId || loadingUpdateUserEmail || loadingUpdateUserProfile
  }), [loadingUpdateUserId, loadingUpdateUserEmail, loadingUpdateUserProfile, updateUserId, updateUserEmail, updateUserProfile]);
  if (!isInitialized) {
    return fallback !== null && fallback !== void 0 ? fallback : null;
  }
  return _jsx(BaseStatsigProvider, {
    client: client,
    children: _jsx(ExperimentContext.Provider, {
      value: value,
      children: children
    })
  });
};